import { type FC, useRef, useState } from 'react';

import DictionaryTrigger from '@helsenorge/designsystem-react/components/DictionaryTrigger';
import HelpBubble from '@helsenorge/designsystem-react/components/HelpBubble';

import { useOutsideEvent } from '@helsenorge/designsystem-react';
import SafeHTML from '@helsenorge/framework-utils/components/safe-html';

import { useAppSelector } from '../../redux/hooks';
import { selectKeywords } from '../../redux/selectors';

import styles from './styles.module.scss';
interface KeywordInfoDisplayProps {
  inputString: string;
  identifier?: string;
}

interface KeywordPartProps {
  keyword: string;
  info: string;
  id: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export const KeywordInfoDisplay: FC<KeywordInfoDisplayProps> = ({ inputString, identifier }) => {
  const keywords = useAppSelector(selectKeywords) || [];
  const [openId, setOpenId] = useState<string | null>(null);

  const escapedKeywords = keywords.map(keyword => keyword.keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`\\b(${escapedKeywords.join('|')})\\b`, 'gi');

  const parts = inputString.split(regex);
  const text = parts.map((part, index) => {
    const key = identifier ? `${identifier}-${index}` : String(index);

    if (index % 2 === 1) {
      const keyword = part;
      const info = keywords.find(kw => kw.keyword === keyword)?.info || 'Mangler informasjon';

      return (
        <KeywordPart
          key={key}
          id={key}
          keyword={keyword}
          info={info}
          isOpen={openId === key}
          onToggle={() => setOpenId(openId === key ? null : key)}
          onClose={() => setOpenId(null)}
        />
      );
    } else {
      return (
        <span key={key}>
          <SafeHTML html={part} />
        </span>
      );
    }
  });

  return (
    <div data-testid="keyword-info-display-container" className={styles['keyword-info-display-container']}>
      {text}
    </div>
  );
};

const KeywordPart: FC<KeywordPartProps> = ({ keyword, info, id, isOpen, onToggle, onClose }) => {
  const controllerRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  useOutsideEvent(bubbleRef as React.RefObject<HTMLDivElement>, onClose);

  return (
    <span key={id}>
      <DictionaryTrigger className={styles.keyword} ref={controllerRef} selected={isOpen} onClick={onToggle}>
        <SafeHTML html={keyword} />
      </DictionaryTrigger>
      <HelpBubble
        ref={bubbleRef}
        className={styles['info-box']}
        variant={'positionabove'}
        role="tooltip"
        onClose={onClose}
        controllerRef={controllerRef as React.RefObject<HTMLButtonElement>}
        showBubble={isOpen}
      >
        <SafeHTML html={info} />
      </HelpBubble>
    </span>
  );
};
